import React, { useState, useEffect } from 'react';
import {
  CurrencyYenIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  DocumentArrowDownIcon,
  CalendarIcon,
  CreditCardIcon,
  XCircleIcon,
  CheckCircleIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import { API_BASE } from '../../lib/config';

const API = `${API_BASE}`;
import { formatCurrency, formatDate } from '../../utils/formatUtils';

const PaymentManagement = ({ onBack }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [payments, setPayments] = useState([]);
  const [revenueStats, setRevenueStats] = useState({
    totalRevenue: 0,
    monthlyRevenue: 0,
    commissionRate: 0,
    totalCommission: 0,
    monthlyCommission: 0,
    stripeFees: 0,
    netProfit: 0,
    netProfitRate: 0,
    totalWorkerPay: 0,
    completedJobs: 0,
    monthlyJobs: 0,
    averageJobValue: 0,
    pendingPayments: 0,
    errorPayments: 0
  });
  const [paymentErrors, setPaymentErrors] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('current_month');
  const [loading, setLoading] = useState(false);
  const [feePercent, setFeePercent] = useState('');
  const [savingFee, setSavingFee] = useState(false);

  useEffect(() => {
    fetchPaymentData();
    if (activeTab === 'overview' || activeTab === 'settings') {
      fetchFeeConfig();
    }
  }, [selectedPeriod, activeTab]);

  const fetchFeeConfig = async () => {
    try {
      // 管理者用（認証必須）
      const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
      const res = await fetch(`${API}/admin/payments/config`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setFeePercent(String(data.fee_percent ?? 20));
        return;
      }
    } catch (e) { }
    // 認証に失敗した場合などは公開用から参照
    try {
      const res2 = await fetch(`${API}/public/payments/config`);
      if (res2.ok) {
        const data2 = await res2.json();
        setFeePercent(String(data2.fee_percent ?? 20));
      }
    } catch (e) { }
  };

  const saveFeeConfig = async () => {
    const fp = parseFloat(feePercent);
    if (isNaN(fp) || fp < 0 || fp > 50) {
      alert('手数料は0〜50%の範囲で入力してください');
      return;
    }
    try {
      setSavingFee(true);
      const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
      const res = await fetch(`${API}/admin/payments/config`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ fee_percent: fp })
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || '手数料の更新に失敗しました');
      }
      alert('手数料を更新しました');
    } catch (e) {
      alert(e.message || '手数料の更新に失敗しました');
    } finally {
      setSavingFee(false);
    }
  };

  const fetchPaymentData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
      const q = selectedPeriod ? `?period=${encodeURIComponent(selectedPeriod)}` : '';
      const [statsRes, listRes] = await Promise.all([
        fetch(`${API}/admin/payments/stats${q}`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/admin/payments/list${q}`, { headers: { 'Authorization': `Bearer ${token}` } }),
      ]);
      if (statsRes.ok) {
        const s = await statsRes.json();
        setRevenueStats({
          totalRevenue: s.totalRevenue || 0,
          monthlyRevenue: s.monthlyRevenue || 0,
          commissionRate: s.commissionRate || 0,
          totalCommission: s.totalCommission || 0,
          monthlyCommission: s.monthlyCommission || 0,
          stripeFees: s.stripeFees || 0,
          netProfit: s.netProfit || 0,
          netProfitRate: s.netProfitRate || 0,
          totalWorkerPay: s.totalWorkerPay || s.monthlyWorkerPay || 0,
          completedJobs: s.completedJobs || 0,
          monthlyJobs: s.monthlyJobs || 0,
          averageJobValue: s.averageJobValue || 0,
          pendingPayments: s.pendingPayments || 0,
          errorPayments: s.errorPayments || 0,
        });
      }
      if (listRes.ok) {
        const lst = await listRes.json();
        setPayments(Array.isArray(lst) ? lst : []);
      }
    } catch (e) {
      // fallback: keep current state
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      completed: { class: 'bg-green-100 text-green-800', label: '完了' },
      processing: { class: 'bg-blue-100 text-blue-800', label: '処理中' },
      failed: { class: 'bg-red-100 text-red-800', label: 'エラー' },
      pending: { class: 'bg-yellow-100 text-yellow-800', label: '保留' }
    };
    const badge = badges[status] || badges.pending;
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${badge.class}`}>
        {badge.label}
      </span>
    );
  };

  const handleRetryPayment = (paymentId) => {
    // 実際の決済再試行処理をここに実装
    const updatedErrors = paymentErrors.map(error => {
      if (error.paymentId === paymentId) {
        return { ...error, retryCount: error.retryCount + 1 };
      }
      return error;
    });
    setPaymentErrors(updatedErrors);
  };

  const exportCSV = () => {
    if (!payments || payments.length === 0) {
      alert('データがありません');
      return;
    }

    const csvData = payments.map(payment => ({
      '決済ID': payment.id,
      '求人ID': payment.jobId,
      '求人タイトル': payment.jobTitle,
      'ワーカー名': payment.workerName,
      '事業者名': payment.employerName,
      'ワーカー報酬': payment.workerPayment || payment.worker_receive, // Handle both snake/camel if mixed
      '事業者支払額': payment.employerPayment || payment.amount,
      '手数料': payment.commission || payment.fee,
      'ステータス': payment.status,
      '完了日': payment.completedAt || payment.date,
      '支払日': payment.paidAt || ''
    }));

    const header = Object.keys(csvData[0]).join(',') + '\n';
    const rows = csvData.map(obj => {
      return Object.values(obj).map(val => {
        let str = String(val === null || val === undefined ? '' : val);
        if (str.includes(',') || str.includes('\n') || str.includes('"')) {
          str = `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      }).join(',');
    }).join('\n');

    const csv = header + rows;
    const bom = new Uint8Array([0xEF, 0xBB, 0xBF]);
    const blob = new Blob([bom, csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    const filename = `payments_export_${new Date().toISOString().slice(0, 10)}.csv`;

    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Generate chart data from payments
  const revenueChartData = React.useMemo(() => {
    if (!payments || payments.length === 0) return [];

    // Aggregate payments by date
    const dailyData = {};
    payments.forEach(p => {
      const date = p.created_at ? p.created_at.slice(0, 10) : p.date;
      if (!date) return;
      if (!dailyData[date]) {
        dailyData[date] = { date, revenue: 0, commission: 0, count: 0 };
      }
      dailyData[date].revenue += (p.amount || 0);
      dailyData[date].commission += (p.commission || p.platform_fee || 0);
      dailyData[date].count += 1;
    });

    // Sort by date and return last 30 days
    return Object.values(dailyData)
      .sort((a, b) => a.date.localeCompare(b.date))
      .slice(-30);
  }, [payments]);

  return (
    <div className="space-y-6">
      {/* 戻るボタン */}
      {onBack && (
        <div className="flex items-center">
          <button
            onClick={onBack}
            className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            メニューに戻る
          </button>
        </div>
      )}

      {activeTab === 'settings' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 max-w-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">決済設定</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">手数料率（%）</label>
                <input
                  type="number"
                  min="0"
                  max="50"
                  step="0.5"
                  value={feePercent}
                  onChange={(e) => setFeePercent(e.target.value)}
                  className="w-40 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">0〜50%の範囲で設定できます。現在の支払いフローに即時反映されます。</p>
              </div>
              <div>
                <button
                  onClick={saveFeeConfig}
                  disabled={savingFee}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {savingFee ? '保存中...' : '保存する'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* タブ */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'overview', label: '売上概要' },
            { id: 'payments', label: '決済履歴' },
            { id: 'errors', label: '決済エラー' },
            { id: 'reports', label: 'レポート' },
            { id: 'settings', label: '設定' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === tab.id
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* 売上統計カード */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CurrencyYenIcon className="w-6 h-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">月間事業者決済売上</p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(revenueStats.monthlyRevenue)}</p>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <ChartBarIcon className="w-6 h-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">月間運営管理者売上</p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(revenueStats.monthlyCommission)}</p>
                  {feePercent !== '' && (
                    <p className="text-xs text-gray-500 mt-1">手数料率: {feePercent}%</p>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <CurrencyYenIcon className="w-6 h-6 text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">月間求職者給料支払い</p>
                  <p className="text-2xl font-bold text-gray-900">{formatCurrency(revenueStats.monthlyWorkerPay)}</p>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center">
                <div className="p-2 bg-red-100 rounded-lg">
                  <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">決済エラー</p>
                  <p className="text-2xl font-bold text-gray-900">{revenueStats.errorPayments || 0}件</p>
                </div>
              </div>
            </div>
          </div>

          {/* Stripe手数料と純利益 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-orange-700">Stripe決済手数料（推定）</p>
                  <p className="text-2xl font-bold text-orange-900">{formatCurrency(revenueStats.stripeFees || 0)}</p>
                  <p className="text-xs text-orange-600 mt-1">約3.6%の決済手数料</p>
                </div>
                <CreditCardIcon className="w-10 h-10 text-orange-400" />
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-700">純利益（手数料控除後）</p>
                  <p className="text-2xl font-bold text-green-900">{formatCurrency(revenueStats.netProfit || 0)}</p>
                  <p className="text-xs text-green-600 mt-1">
                    実質利益率: {revenueStats.netProfitRate || 0}%
                  </p>
                </div>
                <CheckCircleIcon className="w-10 h-10 text-green-400" />
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-700">求職者への総支払い</p>
                  <p className="text-2xl font-bold text-blue-900">{formatCurrency(revenueStats.totalWorkerPay || 0)}</p>
                  <p className="text-xs text-blue-600 mt-1">Stripe Connect経由で直接入金</p>
                </div>
                <CurrencyYenIcon className="w-10 h-10 text-blue-400" />
              </div>
            </div>
          </div>

          {/* 売上グラフ */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">月間売上推移</h3>
            <div className="h-64 flex items-end justify-between space-x-2">
              {revenueChartData.map((data, index) => (
                <div key={index} className="flex flex-col items-center flex-1">
                  <div className="w-full bg-gray-200 rounded-t relative" style={{ height: '200px' }}>
                    <div
                      className="bg-blue-500 rounded-t absolute bottom-0 w-full"
                      style={{ height: `${((data.revenue || 0) / 500000) * 100}%` }}
                    ></div>
                    <div
                      className="bg-green-500 rounded-t absolute bottom-0 w-full"
                      style={{ height: `${((data.commission || 0) / 500000) * 100}%` }}
                    ></div>
                  </div>
                  <div className="mt-2 text-xs text-gray-600">{data.month}</div>
                  <div className="text-xs text-gray-500">{formatCurrency(data.revenue || 0)}</div>
                </div>
              ))}
            </div>
            <div className="flex justify-center mt-4 space-x-4">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-blue-500 rounded mr-2"></div>
                <span className="text-sm text-gray-600">総売上</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded mr-2"></div>
                <span className="text-sm text-gray-600">手数料</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'payments' && (
        <div className="space-y-6">
          {/* フィルターとエクスポート */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
              <div className="flex items-center space-x-4">
                <select
                  value={selectedPeriod}
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="current_month">今月</option>
                  <option value="last_month">先月</option>
                  <option value="last_3_months">過去3ヶ月</option>
                  <option value="all">すべて</option>
                </select>
              </div>
              <button
                onClick={exportCSV}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center"
              >
                <DocumentArrowDownIcon className="w-4 h-4 mr-2" />
                CSV エクスポート
              </button>
            </div>
          </div>

          {/* 決済履歴テーブル（API応答を表示） */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      関係者
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      金額詳細
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ステータス
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      日付
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      決済ID
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {payments.map((p) => (
                    <tr key={p.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="text-sm">
                          <div className="text-gray-900">求職者ID: {p.job_seeker_id}</div>
                          <div className="text-gray-500">事業者ID: {p.employer_id}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm">
                          <div className="text-gray-900">求職者受取: {formatCurrency(p.job_seeker_amount)}</div>
                          <div className="text-gray-500">事業者支払総額: {formatCurrency(p.employer_total_payment)}</div>
                          <div className="text-green-600 font-medium">手数料: {formatCurrency(p.commission)}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(p.status)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{formatDate(p.created_at)}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-xs text-gray-500">{p.id}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'errors' && (
        <div className="space-y-6">
          {/* エラー統計 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <div className="flex items-center">
                <XCircleIcon className="w-8 h-8 text-red-600 mr-3" />
                <div>
                  <p className="text-sm font-medium text-red-900">決済エラー</p>
                  <p className="text-2xl font-bold text-red-600">{paymentErrors.length}件</p>
                </div>
              </div>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
              <div className="flex items-center">
                <ArrowPathIcon className="w-8 h-8 text-yellow-600 mr-3" />
                <div>
                  <p className="text-sm font-medium text-yellow-900">再試行待ち</p>
                  <p className="text-2xl font-bold text-yellow-600">
                    {paymentErrors.filter(e => e.retryCount < 3).length}件
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <div className="flex items-center">
                <CurrencyYenIcon className="w-8 h-8 text-blue-600 mr-3" />
                <div>
                  <p className="text-sm font-medium text-blue-900">エラー総額</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {formatCurrency(paymentErrors.reduce((sum, error) => sum + (error.amount || 0), 0))}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* エラー一覧 */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">決済エラー一覧</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      求人・事業者
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      エラー内容
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      金額
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      再試行回数
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      発生日
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paymentErrors.map((error) => (
                    <tr key={error.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{error.jobTitle}</div>
                          <div className="text-sm text-gray-500">{error.employerName}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm text-red-600 font-medium">{error.errorType}</div>
                          <div className="text-sm text-gray-500">{error.errorMessage}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{formatCurrency(error.amount)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${error.retryCount >= 3
                          ? 'bg-red-100 text-red-800'
                          : 'bg-yellow-100 text-yellow-800'
                          }`}>
                          {error.retryCount}/3回
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(error.occurredAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {error.retryCount < 3 && (
                          <button
                            onClick={() => handleRetryPayment(error.paymentId)}
                            className="text-blue-600 hover:text-blue-700 flex items-center"
                          >
                            <ArrowPathIcon className="w-4 h-4 mr-1" />
                            再試行
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'reports' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">レポート生成</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <button className="bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg p-6 transition-colors">
                <DocumentArrowDownIcon className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                <p className="text-sm font-medium text-blue-900">月次売上レポート</p>
                <p className="text-xs text-blue-600">PDF形式でダウンロード</p>
              </button>
              <button className="bg-green-50 hover:bg-green-100 border border-green-200 rounded-lg p-6 transition-colors">
                <ChartBarIcon className="w-8 h-8 text-green-600 mx-auto mb-2" />
                <p className="text-sm font-medium text-green-900">決済分析レポート</p>
                <p className="text-xs text-green-600">Excel形式でダウンロード</p>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentManagement;